import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { 
  MapPin, Calendar, Users, Star, Wifi, Car, Coffee, Waves,
  Dumbbell, Loader2, Clock, Check, ChevronRight, Shield,
  CreditCard, Building, Image, Utensils, Wind, Tv, Bath,
  Phone, Sparkles
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { api } from '@/App';
import PortalHeader, { isPortalLoggedIn, getPortalGuest } from './PortalHeader';
import PortalDateRangePicker from './PortalDateRangePicker';
import MizuDialer from '@/components/MizuDialer';

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const AMENITY_ICONS = {
  'wifi': Wifi, 'free wifi': Wifi, 'wi-fi': Wifi,
  'parking': Car, 'free parking': Car,
  'restaurant': Utensils, 'dining': Utensils,
  'swimming pool': Waves, 'pool': Waves,
  'gym': Dumbbell, 'fitness': Dumbbell, 'fitness center': Dumbbell,
  'air conditioning': Wind, 'ac': Wind,
  'television': Tv, 'tv': Tv,
  'room service': Coffee, 'coffee': Coffee,
  'spa': Bath, 'spa & wellness': Bath,
  'concierge': Phone, '24/7 support': Phone,
};

const getAmenityIcon = (name) => {
  const lower = name?.toLowerCase() || '';
  for (const [key, icon] of Object.entries(AMENITY_ICONS)) {
    if (lower.includes(key)) return icon;
  }
  return Check;
};

const BookingPortalProperty = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { propertyId } = useParams();
  const [searchParams] = useSearchParams();
  
  // Initialize portal settings from cache to prevent flash
  const [portalSettings, setPortalSettings] = useState(() => {
    try {
      const cached = localStorage.getItem('portal_settings_cache');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed.primary_color && parsed.secondary_color) {
          return { show_competitor_prices: false, ...parsed };
        }
      }
    } catch (e) {}
    return {
      portal_name: 'Book Your Stay', primary_color: '#d4a373', secondary_color: '#2d3748',
      show_competitor_prices: false
    };
  });
  
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Check for Quick Book mode
  const isQuickBook = searchParams.get('quick_book') === 'true';
  const preSelectedRoomTypeId = searchParams.get('room_type_id') || '';
  
  const [bookingForm, setBookingForm] = useState({
    checkIn: searchParams.get('check_in') || '',
    checkOut: searchParams.get('check_out') || '',
    guests: searchParams.get('guests') || '2',
    roomTypeId: preSelectedRoomTypeId,
    numRooms: 1
  });
  
  const [dateRange, setDateRange] = useState(() => {
    const ci = searchParams.get('check_in');
    const co = searchParams.get('check_out');
    return {
      from: ci ? new Date(ci + 'T00:00:00') : undefined,
      to: co ? new Date(co + 'T00:00:00') : undefined,
    };
  });
  
  const [availability, setAvailability] = useState(null);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [specialRequests, setSpecialRequests] = useState('');
  const [booking, setBooking] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('pay_at_hotel');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [dynamicPrices, setDynamicPrices] = useState({});
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [showAllAmenities, setShowAllAmenities] = useState(false);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const [quickBookTriggered, setQuickBookTriggered] = useState(false);
  
  // Guest details for booking (editable, not auto-filled from profile)
  const [guestDetails, setGuestDetails] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const hasDates = !!(bookingForm.checkIn && bookingForm.checkOut);

  useEffect(() => {
    if (dateRange?.from) {
      const ci = format(dateRange.from, 'yyyy-MM-dd');
      const co = dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : '';
      setBookingForm(prev => ({ ...prev, checkIn: ci, checkOut: co }));
    }
  }, [dateRange]);

  useEffect(() => {
    if (bookingForm.checkIn && bookingForm.checkOut) {
      fetchPropertyDetails(bookingForm.checkIn, bookingForm.checkOut);
    } else {
      fetchPropertyDetails();
    }
    fetchPaymentConfig();
  }, [propertyId]);

  // Quick Book: Auto-trigger booking when property loads with pre-selected room
  useEffect(() => {
    if (isQuickBook && property && preSelectedRoomTypeId && hasDates && !quickBookTriggered && !loading) {
      setQuickBookTriggered(true);
      // Set the room and trigger booking
      setBookingForm(prev => ({ ...prev, roomTypeId: preSelectedRoomTypeId }));
      // Small delay to ensure state is set
      setTimeout(() => {
        handleBookNow(preSelectedRoomTypeId);
      }, 100);
    }
  }, [isQuickBook, property, preSelectedRoomTypeId, hasDates, loading, quickBookTriggered]);

  const fetchPaymentConfig = async () => {
    try {
      const response = await api.get('/portal/payment/config');
      setPaymentConfig(response.data);
      if (response.data.razorpay_enabled && response.data.default_payment_mode !== 'pay_at_hotel') {
        setSelectedPaymentMethod('razorpay');
      }
    } catch (error) { console.error('Failed to fetch payment config:', error); }
  };

  const fetchPropertyDetails = async (checkIn = null, checkOut = null) => {
    try {
      let url = `/portal/properties/${propertyId}`;
      if (checkIn && checkOut) url += `?check_in=${checkIn}&check_out=${checkOut}`;
      const [settingsRes, propertyRes] = await Promise.all([
        api.get('/portal/settings'), api.get(url)
      ]);
      const newSettings = { ...portalSettings, ...settingsRes.data };
      setPortalSettings(newSettings);
      // Cache settings
      localStorage.setItem('portal_settings_cache', JSON.stringify(newSettings));
      setProperty(propertyRes.data);
      if (propertyRes.data?.room_types?.length > 0 && checkIn && checkOut) {
        await Promise.all([fetchDynamicPrices(propertyRes.data, checkIn, checkOut), fetchAvailability(checkIn, checkOut)]);
      }
    } catch (error) {
      console.error('Failed to fetch property:', error);
      toast.error('Failed to load property details');
    } finally { setLoading(false); }
  };

  const fetchAvailability = async (checkIn, checkOut) => {
    try {
      const params = new URLSearchParams({ check_in: checkIn, check_out: checkOut, num_rooms: bookingForm.numRooms.toString() });
      const response = await api.get(`/portal/properties/${propertyId}/availability?${params}`);
      setAvailability(response.data);
    } catch (error) { console.error('Failed to fetch availability:', error); }
  };

  const fetchDynamicPrices = async (prop, checkIn, checkOut) => {
    if (!checkIn || !checkOut) return;
    setLoadingPrices(true);
    const results = {};
    await Promise.all(
      prop.room_types.map(async (rt) => {
        try {
          const res = await api.post(`/dynamic-pricing/portal-price?hotel_id=${prop.hotel_id || propertyId}&pricing_rule_id=${rt.pricing_rule_id || ''}`, {
            hotel_name: prop.name || 'Hotel', city: prop.address?.city || prop.city || 'India',
            room_type: rt.name, check_in: checkIn, check_out: checkOut, base_price: rt.base_price || 0
          });
          results[rt.id || rt.room_type_id] = res.data;
        } catch (err) { console.error(`Dynamic pricing error for ${rt.name}:`, err); }
      })
    );
    setDynamicPrices(results);
    setLoadingPrices(false);
  };

  const handleUpdateSearch = async () => {
    if (!bookingForm.checkIn || !bookingForm.checkOut) {
      toast.error('Please select check-in and check-out dates');
      return;
    }
    setLoading(true);
    setBookingForm(prev => ({ ...prev, roomTypeId: '' }));
    await fetchPropertyDetails(bookingForm.checkIn, bookingForm.checkOut);
  };

  const handleSelectRoom = (roomId) => {
    // Toggle: if already selected, deselect; otherwise select the new one
    setBookingForm(prev => ({
      ...prev,
      roomTypeId: prev.roomTypeId === roomId ? '' : roomId
    }));
  };

  const handleBookNow = (roomId) => {
    const targetRoomId = roomId || bookingForm.roomTypeId;
    if (!targetRoomId) { toast.error('Please select a room type'); return; }
    if (!hasDates) { toast.error('Please select check-in and check-out dates first'); return; }
    setBookingForm(prev => ({ ...prev, roomTypeId: targetRoomId }));
    if (!isPortalLoggedIn()) {
      const currentPath = location.pathname + location.search;
      toast.info('Please log in to continue');
      navigate(`/login?returnUrl=${encodeURIComponent(currentPath)}`);
      return;
    }
    // Pre-fill guest details from profile (user can edit)
    const profile = getPortalGuest();
    if (profile) {
      setGuestDetails({
        name: profile.name || '',
        email: profile.email || '',
        phone: profile.phone || ''
      });
    }
    setShowBookingDialog(true);
  };

  const handleRazorpayPayment = useCallback(async (bookingResponse) => {
    const advanceAmount = bookingResponse.advance_required;
    const bookingIds = bookingResponse.bookings.map(b => b.booking_id);
    const loaded = await loadRazorpayScript();
    if (!loaded) { toast.error('Failed to load payment gateway'); return false; }
    try {
      const orderResponse = await api.post('/portal/payment/create-order', { booking_ids: bookingIds, amount: advanceAmount });
      const options = {
        key: orderResponse.data.key_id, amount: orderResponse.data.amount,
        currency: orderResponse.data.currency, order_id: orderResponse.data.order_id,
        name: portalSettings.portal_name || 'Book Your Stay',
        description: `Booking at ${property?.name}`,
        prefill: { name: guestDetails.name, email: guestDetails.email, contact: guestDetails.phone },
        theme: { color: portalSettings.primary_color || '#f97316' },
        handler: async (response) => {
          try {
            await api.post('/portal/payment/verify', {
              razorpay_order_id: response.razorpay_order_id, razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature, booking_ids: bookingIds
            });
            toast.success('Payment successful!');
            navigate(`/booking-confirmed?booking=${bookingResponse.bookings?.[0]?.booking_number}&paid=true`);
          } catch (error) {
            toast.error('Payment verification failed');
            navigate(`/booking-confirmed?booking=${bookingResponse.bookings?.[0]?.booking_number}&payment_pending=true`);
          }
        },
        modal: { ondismiss: () => {
          toast.info('Payment cancelled.');
          navigate(`/booking-confirmed?booking=${bookingResponse.bookings?.[0]?.booking_number}&payment_pending=true`);
        }}
      };
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      return true;
    } catch (error) { console.error('Payment failed:', error); toast.error('Failed to initiate payment'); return false; }
  }, [portalSettings, property, navigate, guestDetails]);

  const handleConfirmBooking = async () => {
    const guest = getPortalGuest();
    if (!guest) { toast.error('Please log in'); return; }
    
    // Validate guest details entered in the form
    if (!guestDetails.name?.trim()) { toast.error('Please enter guest name'); return; }
    if (!guestDetails.email?.trim()) { toast.error('Please enter guest email'); return; }
    if (!guestDetails.phone?.trim()) { toast.error('Please enter guest phone'); return; }
    
    setBooking(true);
    try {
      const bookingData = {
        property_id: propertyId, room_type_id: bookingForm.roomTypeId,
        check_in: bookingForm.checkIn, check_out: bookingForm.checkOut,
        num_rooms: bookingForm.numRooms, num_guests: parseInt(bookingForm.guests),
        guest_name: guestDetails.name.trim(),
        guest_email: guestDetails.email.trim(),
        guest_phone: guestDetails.phone.trim(),
        special_requests: specialRequests, payment_method: selectedPaymentMethod
      };
      const response = await api.post('/portal/bookings', bookingData);
      if (selectedPaymentMethod === 'razorpay' && response.data.advance_required > 0) {
        setShowBookingDialog(false);
        setProcessingPayment(true);
        await handleRazorpayPayment(response.data);
        setProcessingPayment(false);
      } else {
        setShowBookingDialog(false);
        toast.success('Booking confirmed!');
        navigate(`/booking-confirmed?booking=${response.data.bookings?.[0]?.booking_number}`);
      }
    } catch (error) {
      console.error('Booking failed:', error);
      toast.error(error.response?.data?.detail || 'Booking failed');
    } finally { setBooking(false); }
  };

  const calculateTotal = () => {
    if (!bookingForm.roomTypeId) return 0;
    const room = property?.room_types?.find(r => r.id === bookingForm.roomTypeId || r.room_type_id === bookingForm.roomTypeId);
    const pricing = dynamicPrices[bookingForm.roomTypeId];
    let ppn = pricing?.portal_price || room?.base_price || 0;
    if (!ppn && availability) {
      const ra = availability.room_availability?.find(r => r.room_type_id === bookingForm.roomTypeId);
      if (ra) ppn = ra.base_price || 0;
    }
    const nights = calculateNights();
    return ppn * nights * bookingForm.numRooms;
  };

  const calculateNights = () => {
    if (bookingForm.checkIn && bookingForm.checkOut) {
      return Math.ceil((new Date(bookingForm.checkOut) - new Date(bookingForm.checkIn)) / (1000 * 60 * 60 * 24));
    }
    return 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f2f2f2]">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f2f2f2]">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-700">Property not found</h2>
          <Button className="mt-4" onClick={() => navigate('/')}>Back to Home</Button>
        </div>
      </div>
    );
  }

  const guest = getPortalGuest();
  const nights = calculateNights();
  const rating = property.rating_summary?.overall || 4.2;
  const ratingLabel = rating >= 4.5 ? 'Excellent' : rating >= 4.0 ? 'Very Good' : rating >= 3.5 ? 'Good' : 'Average';
  const reviewCount = property.rating_summary?.review_count || 0;

  return (
    <div className="min-h-screen bg-[#f2f2f2]">
      <PortalHeader portalSettings={portalSettings} showBackButton backPath="/search" />

      {/* Date Search Bar at TOP - sticky */}
      <div className="sticky top-0 z-40 bg-white shadow-sm border-b">
        <div className="max-w-[1200px] mx-auto px-4 py-3">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              <h3 className="font-bold text-gray-900 text-sm">Search Dates & Guests</h3>
              <p className="text-[10px] text-gray-500">Check-in: {property.check_in_time || '2 PM'} | Check-out: {property.check_out_time || '12 PM'}</p>
            </div>
            <div className="flex-1">
              <PortalDateRangePicker 
                dateRange={dateRange} 
                onDateRangeChange={setDateRange} 
                variant="light" 
              />
            </div>
            <div className="w-[100px]">
              <Input type="number" min="1" max="10" value={bookingForm.guests}
                onChange={(e) => setBookingForm(prev => ({ ...prev, guests: e.target.value }))}
                className="h-10 text-sm" data-testid="guests-input"
              />
              <span className="text-[10px] text-gray-500">Adults</span>
            </div>
            <Button 
              onClick={handleUpdateSearch}
              className="h-10 text-white font-bold text-sm px-6 rounded-full"
              style={{ background: 'linear-gradient(93deg, #53b2fe, #065af3)' }}
              data-testid="update-search-btn"
            >
              UPDATE SEARCH
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 py-3">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
          <span className="text-blue-600 cursor-pointer hover:underline" onClick={() => navigate('/')}>Home</span>
          <ChevronRight className="h-3 w-3" />
          <span className="text-blue-600 cursor-pointer hover:underline" onClick={() => navigate('/search')}>
            Hotels{property.city ? ` in ${property.city}` : ''}
          </span>
          <ChevronRight className="h-3 w-3" />
          <span className="text-gray-700">{property.name}</span>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-lg shadow-sm mb-4">
          {/* Photo Gallery - Full Width */}
          <div className="p-6 pb-0">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 inline">{property.name}</h1>
                <span className="ml-2 inline-flex gap-0.5">
                  {[...Array(Math.min(property.star_rating || 4, 5))].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-orange-400 fill-orange-400 inline" />
                  ))}
                </span>
                {property.city && (
                  <p className="text-sm text-blue-600 flex items-center gap-1 mt-1">
                    <MapPin className="h-3.5 w-3.5" /> {property.city}{property.state ? `, ${property.state}` : ''}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <span className="font-bold text-gray-900">{ratingLabel}</span>
                  <p className="text-xs text-gray-500">({reviewCount} ratings)</p>
                </div>
                <div className="bg-blue-600 text-white text-lg font-bold w-12 h-12 rounded-lg flex items-center justify-center">
                  {rating.toFixed(1)}
                </div>
              </div>
            </div>

            <div className="flex gap-2 rounded-lg overflow-hidden" style={{ height: '340px' }}>
              <div className="relative overflow-hidden rounded-lg" style={{ width: '65%' }}>
                {property.photos?.length > 0 ? (
                  <img src={property.photos[0]} alt={property.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    onLoad={(e) => { if (e.target.naturalWidth < 10) { const n = property.photos?.[1]; if (n) e.target.src = n; } }}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                    <Building className="h-20 w-20 text-gray-400" />
                  </div>
                )}
                <div className="absolute bottom-3 left-3 bg-black/70 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5">
                  <Image className="h-3.5 w-3.5" /> {property.photos?.length || 0} Photos
                </div>
              </div>
              <div className="flex flex-col gap-2" style={{ width: '35%' }}>
                {property.photos?.slice(1, 3).map((photo, idx) => (
                  <div key={idx} className="flex-1 overflow-hidden rounded-lg">
                    <img src={photo} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  </div>
                ))}
                {(!property.photos || property.photos.length <= 1) && (
                  <><div className="flex-1 bg-gray-100 rounded-lg flex items-center justify-center"><Building className="h-8 w-8 text-gray-300" /></div>
                  <div className="flex-1 bg-gray-100 rounded-lg flex items-center justify-center"><Building className="h-8 w-8 text-gray-300" /></div></>
                )}
                {property.photos?.length === 2 && (
                  <div className="flex-1 bg-gray-100 rounded-lg flex items-center justify-center"><Building className="h-8 w-8 text-gray-300" /></div>
                )}
              </div>
            </div>
          </div>

          {/* About + Sidebar row */}
          <div className="flex gap-6 p-6">
            <div className="flex-1 min-w-0">
              <div className="mb-4">
                <h2 className="text-lg font-bold text-gray-900 mb-2">About Property</h2>
                <p className={`text-sm text-gray-600 leading-relaxed ${!aboutExpanded ? 'line-clamp-3' : ''}`}>
                  {property.description || 'Modern amenities, excellent service, and a prime location for business and leisure travelers.'}
                </p>
                {property.description?.length > 200 && (
                  <button className="text-sm text-blue-600 font-medium mt-1" onClick={() => setAboutExpanded(!aboutExpanded)}>
                    {aboutExpanded ? 'Show less' : '...Read more'}
                  </button>
                )}
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                <div className="flex items-center gap-4 text-xs text-gray-600">
                  <div className="flex items-center gap-1.5"><Shield className="h-4 w-4 text-blue-600" /><span className="font-medium">Verified Property</span></div>
                  <div className="flex items-center gap-1.5"><Check className="h-4 w-4 text-green-600" /><span>Highly Rated</span></div>
                  <div className="flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-orange-500" /><span>Best Price Guaranteed</span></div>
                </div>
              </div>
            </div>
            <div className="w-[280px] flex-shrink-0 space-y-3">
              <div className="border border-gray-200 rounded-lg p-4 flex items-center gap-3">
                <div className="bg-blue-100 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <span className="font-medium text-gray-900 text-sm">{property.city || property.address}</span>
                  {property.state && <span className="text-xs text-gray-500 block">{property.state}</span>}
                </div>
                <span className="text-xs text-blue-600 font-medium cursor-pointer">See on Map</span>
              </div>
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-gray-400" /><span className="text-gray-600">Check-in: <strong>{property.check_in_time || '2:00 PM'}</strong></span></div>
                  <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-gray-400" /><span className="text-gray-600">Check-out: <strong>{property.check_out_time || '12:00 PM'}</strong></span></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Room Listings - Always visible */}
        <div id="room-listings">
          {hasDates && property.room_types?.length > 0 && (
            <div className="mb-3">
              <h2 className="text-lg font-bold text-gray-900">{property.room_types.length} Room Types Available</h2>
              <p className="text-xs text-gray-500">{nights} night(s) | {bookingForm.guests} Guest(s)</p>
            </div>
          )}

          {!hasDates && property.room_types?.length > 0 && (
            <div className="mb-3">
              <h2 className="text-lg font-bold text-gray-900">{property.room_types.length} Room Types</h2>
              <p className="text-xs text-orange-600">Select dates above to see prices and availability</p>
            </div>
          )}

          <div className="space-y-4">
            {property.room_types?.filter((room) => {
              if (availability?.room_availability) {
                const ra = availability.room_availability.find(a => a.room_type_id === room.id || a.room_type_id === room.room_type_id);
                return ra?.is_available !== false;
              }
              return true;
            }).map((room) => {
              const pricing = dynamicPrices[room.id || room.room_type_id];
              const hasDiscount = pricing && pricing.discount_percent > 0;
              const displayPrice = hasDiscount ? pricing.portal_price : room.base_price;
              const roomAvail = availability?.room_availability?.find(a => a.room_type_id === room.id || a.room_type_id === room.room_type_id);
              const availCount = roomAvail?.available_rooms ?? room.available_count;
              const isSelected = bookingForm.roomTypeId === room.id;
              const roomAmenities = room.amenities || property.amenities || [];

              return (
                <div key={room.id} className={`bg-white rounded-lg shadow-sm overflow-hidden transition-all ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
                  data-testid={`room-card-${room.id}`}>
                  <div className="flex">
                    {/* Room Image */}
                    <div className="w-[240px] flex-shrink-0">
                      <div className="h-[200px] bg-gray-200">
                        {room.photos?.[0] ? (
                          <img src={room.photos[0]} alt={room.name} className="w-full h-full object-cover" />
                        ) : property.photos?.[0] ? (
                          <img src={property.photos[0]} alt={room.name} className="w-full h-full object-cover opacity-80"
                            onLoad={(e) => { if (e.target.naturalWidth < 10) { const n = property.photos?.[1]; if (n) e.target.src = n; } }} />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Building className="h-10 w-10 text-gray-400" /></div>
                        )}
                      </div>
                      <div className="p-3">
                        <h4 className="font-bold text-sm text-gray-900">{room.name}</h4>
                        <p className="text-[10px] text-gray-500 flex items-center gap-1">
                          <Users className="h-3 w-3" /> Max {room.max_occupancy || 2} guests
                          {room.bed_type && <span> | {room.bed_type}</span>}
                        </p>
                      </div>
                    </div>

                    {/* Room Details + Amenities */}
                    <div className="flex-1 p-4 flex flex-col justify-between border-l border-gray-100">
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">{room.name}</h4>
                        {room.description && <p className="text-xs text-gray-500 mb-2">{room.description}</p>}
                        <div className="space-y-1 mb-3">
                          <div className="flex items-center gap-1.5 text-xs text-green-700">
                            <Check className="h-3.5 w-3.5" /> Free Cancellation till 24 hrs before check in
                          </div>
                          {room.pricing_start_date && (
                            <div className="text-xs text-orange-600">Special rate: {room.pricing_start_date} to {room.pricing_end_date}</div>
                          )}
                        </div>

                        {/* Amenities at room level */}
                        {roomAmenities.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {roomAmenities.slice(0, 5).map((amenity, idx) => {
                              const Icon = getAmenityIcon(amenity);
                              return (
                                <div key={idx} className="flex items-center gap-1 text-[11px] text-gray-500 bg-gray-50 px-2 py-1 rounded">
                                  <Icon className="h-3 w-3 text-gray-400" />
                                  <span>{amenity}</span>
                                </div>
                              );
                            })}
                            {roomAmenities.length > 5 && (
                              <span className="text-[11px] text-blue-600 self-center">+{roomAmenities.length - 5} more</span>
                            )}
                          </div>
                        )}
                      </div>

                      {portalSettings.show_competitor_prices && pricing?.competitors?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {pricing.competitors.slice(0, 3).map((c, i) => (
                            <span key={i} className="text-[10px] px-2 py-0.5 bg-gray-100 rounded-full text-gray-500 line-through">
                              {c.platform}: ₹{c.price_per_night?.toLocaleString('en-IN')}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Price + Book */}
                    <div className="w-[200px] flex-shrink-0 p-4 text-right border-l border-gray-100 flex flex-col justify-between">
                      <div>
                        {hasDates ? (
                          <>
                            {hasDiscount && <div className="text-xs text-gray-400 line-through">₹ {room.base_price?.toLocaleString('en-IN')}</div>}
                            <div className="text-2xl font-extrabold text-gray-900">₹ {displayPrice?.toLocaleString('en-IN')}</div>
                            <div className="text-[10px] text-gray-400">+₹ {Math.round((displayPrice || 0) * 0.18).toLocaleString('en-IN')} Taxes per room/night</div>
                            {hasDiscount && (
                              <Badge className="mt-1 bg-green-600 text-white text-[10px]">{pricing.discount_percent}% OFF</Badge>
                            )}
                            {loadingPrices && !pricing && (
                              <div className="flex items-center justify-end gap-1 text-[10px] text-orange-500 mt-1">
                                <Loader2 className="h-3 w-3 animate-spin" /> Finding best price...
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-sm text-gray-400 italic">Select dates for price</div>
                        )}
                      </div>
                      <div className="mt-3">
                        {availCount > 0 && availCount <= 5 && (
                          <div className="text-[10px] text-red-500 font-medium mb-1">Only {availCount} left!</div>
                        )}
                        {isSelected ? (
                          <div className="space-y-2">
                            <Button className="w-full font-bold text-sm text-white" 
                              style={{ background: 'linear-gradient(to right, #f97316, #ef4444)' }}
                              onClick={(e) => { e.stopPropagation(); handleBookNow(room.id); }}
                              data-testid={`book-now-${room.id}`}>
                              BOOK NOW
                            </Button>
                            <Button variant="outline" size="sm" className="w-full text-xs text-gray-500"
                              onClick={(e) => { e.stopPropagation(); handleSelectRoom(room.id); }}>
                              Change Room
                            </Button>
                          </div>
                        ) : (
                          <Button className="w-full font-bold text-sm text-white"
                            style={{ background: hasDates ? 'linear-gradient(93deg, #53b2fe, #065af3)' : '#9ca3af' }}
                            disabled={!hasDates}
                            onClick={(e) => { e.stopPropagation(); handleSelectRoom(room.id); }}
                            data-testid={`select-room-${room.id}`}>
                            SELECT ROOM
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Booking Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Calendar className="h-5 w-5" /> Confirm Booking</DialogTitle>
            <DialogDescription>Enter guest details and review booking</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Editable Guest Details */}
            <div className="space-y-3 p-3 bg-gray-50 rounded-lg border">
              <p className="text-sm font-medium text-gray-700 mb-2">Guest Details</p>
              <div>
                <Label htmlFor="guest-name" className="text-xs text-gray-500">Full Name *</Label>
                <Input 
                  id="guest-name"
                  value={guestDetails.name}
                  onChange={(e) => setGuestDetails(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter guest name"
                  className="mt-1"
                  data-testid="guest-name-input"
                />
              </div>
              <div>
                <Label htmlFor="guest-email" className="text-xs text-gray-500">Email *</Label>
                <Input 
                  id="guest-email"
                  type="email"
                  value={guestDetails.email}
                  onChange={(e) => setGuestDetails(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Enter email address"
                  className="mt-1"
                  data-testid="guest-email-input"
                />
              </div>
              <div>
                <Label htmlFor="guest-phone" className="text-xs text-gray-500">Phone *</Label>
                <Input 
                  id="guest-phone"
                  value={guestDetails.phone}
                  onChange={(e) => setGuestDetails(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="Enter phone number"
                  className="mt-1"
                  data-testid="guest-phone-input"
                />
              </div>
            </div>
            
            {/* Booking Summary */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Property</span><span className="font-medium">{property?.name}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Room Type</span><span className="font-medium">{property?.room_types?.find(r => r.id === bookingForm.roomTypeId)?.name}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Check-in</span><span className="font-medium">{bookingForm.checkIn}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Check-out</span><span className="font-medium">{bookingForm.checkOut}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">{nights} night(s) x {bookingForm.numRooms} room(s)</span></div>
              <div className="flex justify-between pt-2 border-t"><span className="font-medium">Total</span><span className="font-bold text-lg text-orange-600">₹{calculateTotal().toLocaleString('en-IN')}</span></div>
            </div>
            <div className="pt-2">
              <Label className="mb-3 block">Payment Method</Label>
              <RadioGroup value={selectedPaymentMethod} onValueChange={setSelectedPaymentMethod} className="space-y-2">
                {paymentConfig?.razorpay_enabled && (
                  <div className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer ${selectedPaymentMethod === 'razorpay' ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`}
                    onClick={() => setSelectedPaymentMethod('razorpay')}>
                    <RadioGroupItem value="razorpay" id="razorpay" />
                    <div className="flex-1"><div className="flex items-center gap-2"><CreditCard className="h-4 w-4 text-orange-500" /><span className="font-medium">Pay Online</span></div></div>
                  </div>
                )}
                <div className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer ${selectedPaymentMethod === 'pay_at_hotel' ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`}
                  onClick={() => setSelectedPaymentMethod('pay_at_hotel')}>
                  <RadioGroupItem value="pay_at_hotel" id="pay_at_hotel" />
                  <div className="flex-1"><div className="flex items-center gap-2"><Building className="h-4 w-4 text-gray-500" /><span className="font-medium">Pay at Hotel</span></div></div>
                </div>
              </RadioGroup>
            </div>
            <div><Label>Special Requests (Optional)</Label><Textarea value={specialRequests} onChange={(e) => setSpecialRequests(e.target.value)} placeholder="Any special requirements..." className="mt-1" rows={3} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookingDialog(false)}>Cancel</Button>
            <Button onClick={handleConfirmBooking} disabled={booking}
              style={{ background: 'linear-gradient(to right, #f97316, #ef4444)' }}
              data-testid="confirm-booking-btn">
              {booking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              {selectedPaymentMethod === 'razorpay' ? 'Pay & Confirm' : 'Confirm Booking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {processingPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 text-center">
            <Loader2 className="h-12 w-12 mx-auto animate-spin text-orange-500 mb-4" />
            <p className="text-lg font-medium">Processing Payment...</p>
            <p className="text-sm text-gray-500 mt-2">Please do not close this window</p>
          </div>
        </div>
      )}
      
      {/* Mizu Dialer for property calls */}
      {(property?.hotel_id || property?.id) && (
        <MizuDialer 
          hotelId={property.hotel_id || property.id} 
          isPortal={true}
          guestCredentials={null} // Guest credentials would be fetched when dialer opens
        />
      )}
    </div>
  );
};

export default BookingPortalProperty;
